import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Brain, 
  Play, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Trophy,
  Target,
  BarChart3,
  Star,
  AlertCircle,
  BookOpen,
  TrendingUp,
  Award,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { getStudentEnrollments } from '../../services/elearningStudentService';
import { getStudentProfileByToken } from '../../services/elearningStudentService';
import { useTenantContext } from '../../context/TenantContext';
import { useToast } from '../../hooks/useToast';
import StudentLayout from '../../components/student/StudentLayout';

interface Quiz {
  id: string;
  title: string;
  description: string;
  courseId: string;
  courseName: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  questionsCount: number;
  duration: number; // en minutes
  maxAttempts: number;
  passingScore: number;
  isCompleted: boolean;
  bestScore?: number;
  attemptsUsed: number;
  lastAttemptDate?: string;
  category: string;
}

interface QuizAttempt {
  id: string;
  quizId: string;
  score: number;
  totalQuestions: number;
  correctAnswers: number;
  timeSpent: number; // en secondes
  completedAt: string;
  passed: boolean;
}

const StudentQuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  
  const [studentProfile, setStudentProfile] = useState<any>(null);
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');

  // Données des quiz simulées
  const [quizzes, setQuizzes] = useState<Quiz[]>([
    {
      id: '1',
      title: 'Fondamentaux JavaScript',
      description: 'Testez vos connaissances sur les bases de JavaScript : variables, fonctions, boucles',
      courseId: 'js-101',
      courseName: 'JavaScript pour débutants',
      difficulty: 'beginner',
      questionsCount: 15,
      duration: 20,
      maxAttempts: 3,
      passingScore: 70,
      isCompleted: true,
      bestScore: 85,
      attemptsUsed: 2,
      lastAttemptDate: '2024-01-25',
      category: 'Programmation'
    },
    {
      id: '2',
      title: 'HTML & CSS Avancé',
      description: 'Quiz sur les concepts avancés de HTML5 et CSS3 : flexbox, grid, animations',
      courseId: 'web-101',
      courseName: 'Développement Web',
      difficulty: 'intermediate',
      questionsCount: 20,
      duration: 30,
      maxAttempts: 2,
      passingScore: 75,
      isCompleted: false,
      attemptsUsed: 0,
      category: 'Web Design'
    },
    {
      id: '3',
      title: 'React Hooks & Context',
      description: 'Évaluez votre maîtrise des hooks React et de l\'API Context',
      courseId: 'react-101',
      courseName: 'React Fondamentaux',
      difficulty: 'advanced',
      questionsCount: 12,
      duration: 25,
      maxAttempts: 2,
      passingScore: 80,
      isCompleted: true,
      bestScore: 92,
      attemptsUsed: 1,
      lastAttemptDate: '2024-01-20',
      category: 'Frameworks'
    },
    {
      id: '4',
      title: 'Python Structures de Données',
      description: 'Quiz sur les listes, dictionnaires, sets et tuples en Python',
      courseId: 'python-101',
      courseName: 'Python pour débutants',
      difficulty: 'beginner',
      questionsCount: 18,
      duration: 35,
      maxAttempts: 3,
      passingScore: 70,
      isCompleted: false,
      attemptsUsed: 1,
      lastAttemptDate: '2024-01-22',
      category: 'Programmation'
    }
  ]);

  // Historique des tentatives simulé
  const [quizAttempts, setQuizAttempts] = useState<QuizAttempt[]>([
    {
      id: '1',
      quizId: '1',
      score: 85,
      totalQuestions: 15,
      correctAnswers: 13,
      timeSpent: 1200,
      completedAt: '2024-01-25T14:30:00',
      passed: true
    },
    {
      id: '2',
      quizId: '3',
      score: 92,
      totalQuestions: 12,
      correctAnswers: 11,
      timeSpent: 1500,
      completedAt: '2024-01-20T10:15:00',
      passed: true
    }
  ]);

  const difficulties = [
    { value: 'beginner', label: 'Débutant', color: 'bg-green-500' },
    { value: 'intermediate', label: 'Intermédiaire', color: 'bg-yellow-500' },
    { value: 'advanced', label: 'Avancé', color: 'bg-red-500' }
  ];

  const filters = [
    { value: 'all', label: 'Tous' },
    { value: 'available', label: 'Disponibles' },
    { value: 'completed', label: 'Terminés' },
    { value: 'in_progress', label: 'En cours' }
  ];



  const handleStudentProfileLoaded = (profile: any) => {
    setStudentProfile(profile);
    if (profile?.id) {
      loadEnrollments(profile.id);
    }
  };

  const loadEnrollments = async (studentId: string) => {
    if (!studentId || !tenant?.id) return;

    setIsLoading(true);
    try {
      const enrollmentsData = await getStudentEnrollments(studentId, tenant.id);
      setEnrollments(enrollmentsData);
    } catch (error) {
      console.error('Erreur lors du chargement des inscriptions:', error);
    } finally {
      setIsLoading(false);
    }
  };



  const handleStartQuiz = (quizId: string) => {
    const quiz = quizzes.find(q => q.id === quizId);
    if (!quiz) return;

    if (quiz.attemptsUsed >= quiz.maxAttempts) {
      toast({
        title: 'Limite atteinte',
        description: `Vous avez utilisé toutes vos tentatives (${quiz.maxAttempts}) pour ce quiz.`,
        variant: 'destructive'
      });
      return;
    }

    toast({
      title: 'Quiz démarré',
      description: `Démarrage du quiz "${quiz.title}". Bonne chance !`
    });

    // Ici, on redirigerait vers la page de quiz
    // navigate(`/elearning/quiz/${quizId}`);
  };

  const filteredQuizzes = quizzes.filter(quiz => {
    const matchesFilter = 
      selectedFilter === 'all' ||
      (selectedFilter === 'available' && !quiz.isCompleted && quiz.attemptsUsed < quiz.maxAttempts) ||
      (selectedFilter === 'completed' && quiz.isCompleted) ||
      (selectedFilter === 'in_progress' && quiz.attemptsUsed > 0 && !quiz.isCompleted);
    
    const matchesDifficulty = selectedDifficulty === 'all' || quiz.difficulty === selectedDifficulty;
    
    return matchesFilter && matchesDifficulty;
  });

  const getDifficultyConfig = (difficulty: Quiz['difficulty']) => {
    return difficulties.find(d => d.value === difficulty) || difficulties[0];
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
  };

  const formatTimeSpent = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Statistiques
  const totalQuizzes = quizzes.length;
  const completedQuizzes = quizzes.filter(q => q.isCompleted).length;
  const averageScore = quizzes
    .filter(q => q.bestScore !== undefined)
    .reduce((sum, q) => sum + (q.bestScore || 0), 0) / Math.max(1, quizzes.filter(q => q.bestScore !== undefined).length);
  const totalAttempts = quizAttempts.length;

  return (
    <StudentLayout
      title="Quiz & Tests"
      description="Testez vos connaissances et suivez vos performances"
      onStudentProfileLoaded={handleStudentProfileLoaded}
    >
      <SEOMetaTags 
        title="Quiz & Tests - E-Learning"
        description="Testez vos connaissances avec des quiz interactifs et suivez vos performances."
        keywords="quiz, tests, évaluation, connaissances, e-learning"
      />

          {/* Statistiques */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total quiz</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalQuizzes}</p>
                  </div>
                  <Brain className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Terminés</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{completedQuizzes}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Score moyen</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {Math.round(averageScore || 0)}%
                    </p>
                  </div>
                  <Star className="h-8 w-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Tentatives</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalAttempts}</p>
                  </div>
                  <Target className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filtres */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-4 mb-8">
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
              {filters.map(filter => (
                <Button
                  key={filter.value}
                  variant={selectedFilter === filter.value ? 'default' : 'outline'}
                  size="sm" className="w-full sm:w-auto"
                  onClick={() => setSelectedFilter(filter.value)}
                >
                  {filter.label}
                </Button>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
              <Button
                variant={selectedDifficulty === 'all' ? 'default' : 'outline'}
                size="sm" className="w-full sm:w-auto"
                onClick={() => setSelectedDifficulty('all')}
              >
                Toutes difficultés
              </Button>
              {difficulties.map(difficulty => (
                <Button
                  key={difficulty.value}
                  variant={selectedDifficulty === difficulty.value ? 'default' : 'outline'}
                  size="sm" className="w-full sm:w-auto"
                  onClick={() => setSelectedDifficulty(difficulty.value)}
                >
                  {difficulty.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Liste des quiz */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredQuizzes.map((quiz, index) => {
              const difficultyConfig = getDifficultyConfig(quiz.difficulty);
              const canAttempt = quiz.attemptsUsed < quiz.maxAttempts;

              return (
                <motion.div
                  key={quiz.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className={`overflow-hidden shadow-lg transition-all duration-300 hover:shadow-xl ${
                    quiz.isCompleted ? 'bg-green-50 dark:bg-green-900/20' : ''
                  }`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-4 space-y-4 sm:space-y-0">
                        <div className="flex-1 text-center sm:text-left">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                              {quiz.title}
                            </h3>
                            {quiz.isCompleted && (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            )}
                          </div>
                          
                          <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                            {quiz.description}
                          </p>
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                            <div className="flex items-center space-x-1">
                              <BookOpen className="h-4 w-4" />
                              <span>{quiz.courseName}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Brain className="h-4 w-4" />
                              <span>{quiz.questionsCount} questions</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Clock className="h-4 w-4" />
                              <span>{formatDuration(quiz.duration)}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-col items-center sm:items-end space-y-2">
                          <Badge className={`${difficultyConfig.color} text-white`}>
                            {difficultyConfig.label}
                          </Badge>
                          <Badge variant="secondary" className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                            {quiz.category}
                          </Badge>
                          {quiz.bestScore !== undefined && (
                            <Badge variant="outline" className="bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200">
                              Meilleur score: {quiz.bestScore}%
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Informations sur les tentatives */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 space-y-4 sm:space-y-0 text-sm text-gray-500 mb-4">
                        <div className="flex items-center space-x-4">
                          <span>Tentatives: {quiz.attemptsUsed}/{quiz.maxAttempts}</span>
                          {quiz.lastAttemptDate && (
                            <span className="ml-2">Dernière: {new Date(quiz.lastAttemptDate).toLocaleDateString()}</span>
                          )}
                        </div>
                        {quiz.isCompleted && quiz.bestScore !== undefined && (
                          <div className="flex items-center space-x-2">
                            <Trophy className="h-4 w-4 text-yellow-500" />
                            <span>Score: {quiz.bestScore}%</span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
                        <div className="flex items-center space-x-2">
                          {quiz.isCompleted && (
                            <Badge className="bg-green-500 text-white">
                              Terminé
                            </Badge>
                          )}
                          {!quiz.isCompleted && quiz.attemptsUsed > 0 && (
                            <Badge variant="outline" className="bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200">
                              En cours
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
                          {canAttempt && (
                            <Button
                              onClick={() => handleStartQuiz(quiz.id)}
                              className="flex items-center space-x-2 w-full sm:w-auto"
                            >
                              <Play className="h-4 w-4" />
                              <span>{quiz.attemptsUsed > 0 ? 'Reprendre' : 'Commencer'}</span>
                            </Button>
                          )}
                          {quiz.isCompleted && (
                            <Button
                              variant="outline" className="w-full sm:w-auto"
                              onClick={() => {
                                toast({
                                  title: 'Afficher les résultats',
                                  description: `Affichage des résultats pour "${quiz.title}" (Fonctionnalité à implémenter).`
                                });
                              }}
                            >
                              <BarChart3 className="h-4 w-4" />
                              <span>Résultats</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Message si aucun quiz */}
          {filteredQuizzes.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <Brain className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Aucun quiz trouvé
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Aucun quiz ne correspond aux filtres sélectionnés.
              </p>
              <Button onClick={() => {
                setSelectedFilter('all');
                setSelectedDifficulty('all');
              }}>
                Réinitialiser les filtres
              </Button>
            </motion.div>
          )}
    </StudentLayout>
  );
};

export default StudentQuizPage;
